import apiClient from '@/utils/axios.js';
import { ref } from 'vue';

const notifications = ref([]);
const unreadCount = ref(0);
let listeningUserId = null;
/**
 * Boot asks for notifications twice: the topbar on mount, and App.vue again once
 * the configuration and addon requests it waits on have resolved. The two are
 * serialized rather than simultaneous, so neither could see the other and every
 * page load ran two full notification queries. The refs are module-scoped —
 * every caller already shares one answer — so sharing the request too changes
 * nothing but the count.
 */
let inFlightFetch = null;
let lastFetchedAt = 0;
/**
 * How recent an answer has to be for a second caller to accept it.
 *
 * Sharing the in-flight promise is not enough on its own, because the two boot
 * callers are *serialized*: the topbar's request has already resolved by the
 * time App.vue reaches its own, having waited on `/configurations/index` and
 * `/addon-features/index` first. So the window has to cover the gap between
 * them rather than only their overlap. Ten seconds is far shorter than anything
 * a user would notice and far longer than a boot takes, and Echo pushes new
 * notifications in the meantime anyway.
 */
const FETCH_FRESHNESS_MS = 10_000;

export function useNotifications() {
    const fetchNotifications = async ({ force = false } = {}) => {
        if (inFlightFetch) return inFlightFetch;
        if (!force && Date.now() - lastFetchedAt < FETCH_FRESHNESS_MS) return undefined;

        inFlightFetch = (async () => {
            try {
                const res = await apiClient.get('/notifications');
                notifications.value = res.data?.data ?? [];
                unreadCount.value = notifications.value.filter((n) => !n.read_at).length;
                lastFetchedAt = Date.now();
            } catch (e) {
                console.error(e);
            }
        })().finally(() => {
            inFlightFetch = null;
        });

        return inFlightFetch;
    };

    const markAsRead = async (id) => {
        try {
            await apiClient.post(`/notifications/${id}/read`);
            const n = notifications.value.find((n) => n.id === id);
            if (n) n.read_at = new Date().toISOString();
            unreadCount.value = notifications.value.filter((n) => !n.read_at).length;
        } catch (e) {
            console.error(e);
        }
    };

    const markAllAsRead = async () => {
        try {
            await apiClient.post('/notifications/read-all');
            notifications.value.forEach((n) => (n.read_at = new Date().toISOString()));
            unreadCount.value = 0;
        } catch (e) {
            console.error(e);
        }
    };

    const deleteNotification = async (id) => {
        try {
            await apiClient.post(`/notifications/${id}/delete`);
            notifications.value = notifications.value.filter((n) => n.id !== id);
            unreadCount.value = notifications.value.filter((n) => !n.read_at).length;
        } catch (e) {
            console.error(e);
        }
    };

    const deleteAllNotifications = async () => {
        try {
            await apiClient.post('/notifications/delete-all');
            notifications.value = [];
            unreadCount.value = 0;
        } catch (e) {
            console.error(e);
        }
    };

    const listenForNotifications = (userId) => {
        if (!userId || !window.Echo) return;
        const normalized = String(userId);
        if (listeningUserId === normalized) return;
        if (listeningUserId != null) {
            window.Echo.leave(`App.Models.User.${listeningUserId}`);
        }

        window.Echo.private(`App.Models.User.${userId}`).notification((data) => {
            notifications.value.unshift({
                id: data.id ?? Date.now(),
                read_at: null,
                data: data,
                created_at: new Date().toISOString(),
            });
            unreadCount.value++;
        });
        listeningUserId = normalized;
    };

    const stopListening = (userId) => {
        if (!userId || !window.Echo) return;
        window.Echo.leave(`App.Models.User.${userId}`);
        if (listeningUserId === String(userId)) listeningUserId = null;
    };

    return {
        notifications,
        unreadCount,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        deleteAllNotifications,
        listenForNotifications,
        stopListening,
    };
}
